// app/src/lib/scale/account.js — team-account identity for the client.
//
// Resolves which ACCOUNT the signed-in user acts in and their role, from the
// ID-token claims stamped by the pre-token-gen Lambda:
//   custom:accountId — the account (owner's sub) whose data this user sees.
//   custom:role      — 'owner' (full read/write) | 'viewer' (read-only, v1).
//
// For an owner, accountId === their own sub. The server (resolveAccount + the
// membership table) is the authoritative gate; this is the UX-layer source.

import { currentClaims } from '../auth/cognitoClient.js';

let _cache = null;

/** @returns {Promise<null | { sub:string, accountId:string, role:'owner'|'viewer', adminEmail:string|null }>} */
export async function currentAccount() {
  const claims = await currentClaims();
  if (!claims) { _cache = null; return null; }
  const sub = claims.sub;
  const accountId = claims['custom:accountId'] || sub;
  const role = claims['custom:role'] === 'viewer' ? 'viewer' : 'owner';
  // For a viewer, the account owner's email is stamped by pre-token-gen so the
  // UI can show who they're sharing access with.
  const adminEmail = role === 'viewer' ? (claims['custom:adminEmail'] || null) : null;
  _cache = { sub, accountId, role, adminEmail };
  return _cache;
}

/** Synchronous best-effort read of the last resolved account (null until first resolve). */
export function currentAccountSync() { return _cache; }

/** True when the signed-in user is a read-only viewer of someone else's account. */
export function isViewer() { return _cache != null && _cache.role === 'viewer'; }
